/**
 * Meeting Notes Page
 *
 * Manager view for managing meeting/conversation notes with employees.
 * Story 6-3: Gespreksverslagen
 */

import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Layout } from '../../components/Layout';
import { useEmployees } from '../employees/useEmployees';
import {
  useMeetingNotes,
  useCreateMeetingNote,
  useUpdateMeetingNote,
  useDeleteMeetingNote,
} from './useNotes';
import type { MeetingNote } from '../../lib/notesApi';

export function MeetingNotesPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedEmployeeId = searchParams.get('employeeId')
    ? parseInt(searchParams.get('employeeId')!)
    : undefined;

  const { data: employees } = useEmployees();
  const { data: notesData, isLoading } = useMeetingNotes(selectedEmployeeId);
  const createNote = useCreateMeetingNote();
  const updateNote = useUpdateMeetingNote();
  const deleteNote = useDeleteMeetingNote();

  const [showNewForm, setShowNewForm] = useState(false);
  const [editingNote, setEditingNote] = useState<MeetingNote | null>(null);
  const [viewingNote, setViewingNote] = useState<MeetingNote | null>(null);
  const [formData, setFormData] = useState({
    employeeId: '',
    meetingDate: new Date().toISOString().split('T')[0],
    summary: '',
    discussedTopics: '',
    actionItems: '',
    employeeComments: '',
    isSharedWithEmployee: false,
  });

  const notes = notesData?.data || [];

  const parseJsonArray = (value: string | null): string[] => {
    if (!value) return [];
    try {
      return JSON.parse(value);
    } catch {
      return [];
    }
  };

  const handleCreate = async () => {
    if (!formData.employeeId || !formData.summary || !formData.meetingDate) return;

    await createNote.mutateAsync({
      employeeId: parseInt(formData.employeeId),
      meetingDate: formData.meetingDate,
      summary: formData.summary,
      discussedTopics: formData.discussedTopics
        ? formData.discussedTopics.split('\n').filter((t) => t.trim())
        : undefined,
      actionItems: formData.actionItems
        ? formData.actionItems.split('\n').filter((a) => a.trim())
        : undefined,
      employeeComments: formData.employeeComments || undefined,
      isSharedWithEmployee: formData.isSharedWithEmployee,
    });

    setShowNewForm(false);
    resetForm();
  };

  const handleUpdate = async () => {
    if (!editingNote) return;

    await updateNote.mutateAsync({
      id: editingNote.id,
      input: {
        summary: formData.summary,
        discussedTopics: formData.discussedTopics
          ? formData.discussedTopics.split('\n').filter((t) => t.trim())
          : undefined,
        actionItems: formData.actionItems
          ? formData.actionItems.split('\n').filter((a) => a.trim())
          : undefined,
        employeeComments: formData.employeeComments || undefined,
        isSharedWithEmployee: formData.isSharedWithEmployee,
      },
    });

    setEditingNote(null);
    resetForm();
  };

  const handleDelete = async (id: number) => {
    if (confirm('Weet je zeker dat je dit gespreksverslag wilt verwijderen?')) {
      await deleteNote.mutateAsync(id);
    }
  };

  const resetForm = () => {
    setFormData({
      employeeId: '',
      meetingDate: new Date().toISOString().split('T')[0],
      summary: '',
      discussedTopics: '',
      actionItems: '',
      employeeComments: '',
      isSharedWithEmployee: false,
    });
  };

  const openEditForm = (note: MeetingNote) => {
    setEditingNote(note);
    setFormData({
      employeeId: note.employeeId.toString(),
      meetingDate: note.meetingDate,
      summary: note.summary,
      discussedTopics: parseJsonArray(note.discussedTopics).join('\n'),
      actionItems: parseJsonArray(note.actionItems).join('\n'),
      employeeComments: note.employeeComments || '',
      isSharedWithEmployee: note.isSharedWithEmployee,
    });
  };

  if (isLoading) {
    return (
      <Layout>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
          <span className="ml-3 text-gray-600">Gespreksverslagen laden...</span>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div data-testid="meeting-notes-page" className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Gespreksverslagen</h1>
            <p className="mt-1 text-sm text-gray-500">
              Documenteer gesprekken en maak ze optioneel zichtbaar voor de medewerker.
            </p>
          </div>
          <button
            onClick={() => setShowNewForm(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Nieuw Verslag
          </button>
        </div>

        {/* Filters */}
        <div className="mb-6 flex items-center gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Filter op medewerker
            </label>
            <select
              value={selectedEmployeeId || ''}
              onChange={(e) => {
                if (e.target.value) {
                  setSearchParams({ employeeId: e.target.value });
                } else {
                  setSearchParams({});
                }
              }}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="">Alle medewerkers</option>
              {employees?.data?.map((emp) => (
                <option key={emp.id} value={emp.id}>
                  {emp.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Notes List */}
        {notes.length === 0 ? (
          <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">Geen gespreksverslagen</h3>
            <p className="mt-1 text-sm text-gray-500">
              Leg een nieuw gesprek vast om te beginnen.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {notes.map((note) => {
              const employee = employees?.data?.find((e) => e.id === note.employeeId);
              const topics = parseJsonArray(note.discussedTopics);
              const actions = parseJsonArray(note.actionItems);

              return (
                <div
                  key={note.id}
                  className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden"
                >
                  <div className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
                          <svg className="w-5 h-5 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                          </svg>
                        </div>
                        <div>
                          <h3 className="font-medium text-gray-900">
                            Gesprek met {employee?.name || `Medewerker #${note.employeeId}`}
                          </h3>
                          <p className="text-sm text-gray-500">
                            {new Date(note.meetingDate).toLocaleDateString('nl-NL', {
                              weekday: 'long',
                              day: 'numeric',
                              month: 'long',
                              year: 'numeric',
                            })}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        {note.isSharedWithEmployee && (
                          <span className="flex items-center gap-1 text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                              <path fillRule="evenodd" d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z" clipRule="evenodd" />
                            </svg>
                            Gedeeld
                          </span>
                        )}
                        <button
                          onClick={() => setViewingNote(note)}
                          className="p-1 text-gray-400 hover:text-gray-600"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => openEditForm(note)}
                          className="p-1 text-gray-400 hover:text-gray-600"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDelete(note.id)}
                          className="p-1 text-gray-400 hover:text-red-600"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>

                    <p className="mt-3 text-gray-700 line-clamp-2">{note.summary}</p>

                    <div className="mt-3 flex items-center gap-4 text-xs text-gray-500">
                      {topics.length > 0 && (
                        <span className="flex items-center gap-1">
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                          </svg>
                          {topics.length} onderwerpen
                        </span>
                      )}
                      {actions.length > 0 && (
                        <span className="flex items-center gap-1">
                          <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
                          </svg>
                          {actions.length} actiepunten
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* View Note Modal */}
        {viewingNote && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">
                      Gesprek met {employees?.data?.find((e) => e.id === viewingNote.employeeId)?.name}
                    </h3>
                    <p className="text-sm text-gray-500">
                      {new Date(viewingNote.meetingDate).toLocaleDateString('nl-NL', {
                        weekday: 'long',
                        day: 'numeric',
                        month: 'long',
                        year: 'numeric',
                      })}
                    </p>
                  </div>
                  <button
                    onClick={() => setViewingNote(null)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <h4 className="text-sm font-medium text-gray-700 mb-2">Samenvatting</h4>
                    <p className="text-gray-700 whitespace-pre-wrap">{viewingNote.summary}</p>
                  </div>

                  {parseJsonArray(viewingNote.discussedTopics).length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Besproken Onderwerpen</h4>
                      <ul className="list-disc list-inside space-y-1">
                        {parseJsonArray(viewingNote.discussedTopics).map((topic, i) => (
                          <li key={i} className="text-gray-700">{topic}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {parseJsonArray(viewingNote.actionItems).length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Actiepunten</h4>
                      <ul className="space-y-2">
                        {parseJsonArray(viewingNote.actionItems).map((action, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <svg className="w-5 h-5 text-blue-500 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="text-gray-700">{action}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {viewingNote.employeeComments && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Opmerkingen Medewerker</h4>
                      <p className="text-gray-700 italic bg-gray-50 p-3 rounded-lg">
                        {viewingNote.employeeComments}
                      </p>
                    </div>
                  )}
                </div>

                <div className="mt-6 flex justify-end">
                  <button
                    onClick={() => setViewingNote(null)}
                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Sluiten
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* New/Edit Note Modal */}
        {(showNewForm || editingNote) && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">
                {editingNote ? 'Verslag Bewerken' : 'Nieuw Gespreksverslag'}
              </h3>

              <div className="space-y-4">
                {!editingNote && (
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Medewerker *
                      </label>
                      <select
                        value={formData.employeeId}
                        onChange={(e) => setFormData({ ...formData, employeeId: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Selecteer medewerker</option>
                        {employees?.data?.map((emp) => (
                          <option key={emp.id} value={emp.id}>
                            {emp.name}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Datum *
                      </label>
                      <input
                        type="date"
                        value={formData.meetingDate}
                        onChange={(e) => setFormData({ ...formData, meetingDate: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    </div>
                  </div>
                )}

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Samenvatting *
                  </label>
                  <textarea
                    rows={3}
                    value={formData.summary}
                    onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                    placeholder="Korte samenvatting van het gesprek..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Besproken Onderwerpen (1 per regel)
                  </label>
                  <textarea
                    rows={3}
                    value={formData.discussedTopics}
                    onChange={(e) => setFormData({ ...formData, discussedTopics: e.target.value })}
                    placeholder="Onderwerp 1&#10;Onderwerp 2&#10;Onderwerp 3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Actiepunten (1 per regel)
                  </label>
                  <textarea
                    rows={3}
                    value={formData.actionItems}
                    onChange={(e) => setFormData({ ...formData, actionItems: e.target.value })}
                    placeholder="Actie 1&#10;Actie 2&#10;Actie 3"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Opmerkingen Medewerker
                  </label>
                  <textarea
                    rows={2}
                    value={formData.employeeComments}
                    onChange={(e) => setFormData({ ...formData, employeeComments: e.target.value })}
                    placeholder="Eventuele opmerkingen van de medewerker..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>

                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="shareWithEmployee"
                    checked={formData.isSharedWithEmployee}
                    onChange={(e) =>
                      setFormData({ ...formData, isSharedWithEmployee: e.target.checked })
                    }
                    className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="shareWithEmployee" className="text-sm text-gray-700">
                    Deel dit verslag met de medewerker
                  </label>
                </div>
              </div>

              <div className="mt-6 flex gap-3 justify-end">
                <button
                  onClick={() => {
                    setShowNewForm(false);
                    setEditingNote(null);
                    resetForm();
                  }}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Annuleren
                </button>
                <button
                  onClick={editingNote ? handleUpdate : handleCreate}
                  disabled={
                    (!editingNote && (!formData.employeeId || !formData.meetingDate)) ||
                    !formData.summary ||
                    createNote.isPending ||
                    updateNote.isPending
                  }
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                >
                  {createNote.isPending || updateNote.isPending
                    ? 'Opslaan...'
                    : editingNote
                    ? 'Bijwerken'
                    : 'Opslaan'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
